// Search page. Markup mirrors 01-search.html line-for-line.
//
// Editable extras versus the static design:
//   - From/To use the AirportPicker (typeahead → /v1/airports/autocomplete/).
//   - Depart/Return use CalendarPicker (react-day-picker) inside a popover.
//   - Travellers · cabin uses a popover with steppers + cabin select.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import { cacheAirport, lookupAirport, type AirportInfo } from "../lib/airports";
import { useAuth } from "../lib/auth";
import { saveSearch } from "../lib/searchCache";
import { AirportPicker } from "../components/AirportPicker";
import { CalendarPicker, dateToYmd, ymdToDate } from "../components/CalendarPicker";
import { SearchingLoader } from "../components/SearchingLoader";
import { SourcePicker } from "../components/SourcePicker";
import { DEFAULT_FLIGHT_SOURCE, type FlightSource, type SearchResponse } from "../lib/types";

type Trip = "one_way" | "round_trip";
type ActiveBox = "from" | "to" | "depart" | "return" | "travellers" | "source" | null;

// `flight_cabin_class` codes are the API's (flights/schemas.py CABIN_CLASS):
// 1=Any 2=Economy 3=Premium Economy 4=Business 5=Premium Business 6=First.
// 1 means "any cabin", which is why the picker starts at Economy (2).
const CABIN_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 2, label: "Economy" },
  { value: 3, label: "Premium economy" },
  { value: 4, label: "Business" },
  { value: 6, label: "First" },
];
const CABIN_ECONOMY = 2;
const CABIN_PREMIUM_ECONOMY = 3;

function defaultDate(daysFromNow: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().slice(0, 10);
}
function fmtPretty(ymd: string): { value: string; sub: string } {
  if (!ymd) return { value: "—", sub: "" };
  const d = new Date(ymd + "T00:00:00");
  if (Number.isNaN(d.getTime())) return { value: "—", sub: "" };
  return {
    value: d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
    sub: d.toLocaleDateString("en-IN", { weekday: "short" }) + " · " + d.getFullYear(),
  };
}

const DEFAULT_FROM: AirportInfo = { code: "DEL", city: "Delhi", name: "Indira Gandhi Intl" };
const DEFAULT_TO:   AirportInfo = { code: "BOM", city: "Mumbai", name: "Chhatrapati Shivaji" };

export function Search() {
  const nav = useNavigate();
  const { me } = useAuth();
  const partnerCurrency = me?.partner?.currency || "INR";

  const [trip, setTrip] = useState<Trip>("one_way");
  const [from, setFrom] = useState<AirportInfo>(DEFAULT_FROM);
  const [to, setTo]     = useState<AirportInfo>(DEFAULT_TO);
  const [outboundDate, setOutboundDate] = useState(defaultDate(7));
  const [inboundDate, setInboundDate]   = useState(defaultDate(14));
  const [adult, setAdult] = useState(1);
  const [child, setChild] = useState(0);
  const [infant, setInfant] = useState(0);
  const [cabin, setCabin] = useState(CABIN_ECONOMY);
  const [source, setSource] = useState<FlightSource>(DEFAULT_FLIGHT_SOURCE);
  const [direct, setDirect] = useState(false);
  const [refundable, setRefundable] = useState(false);
  const [premium, setPremium] = useState(false);

  const [active, setActive] = useState<ActiveBox>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Seed defaults into the airport cache so other pages can look them up.
  useEffect(() => {
    cacheAirport(DEFAULT_FROM);
    cacheAirport(DEFAULT_TO);
  }, []);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!cardRef.current?.contains(e.target as Node)) setActive(null);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  function onTogglePremium() {
    setPremium((p) => {
      const next = !p;
      setCabin(next ? CABIN_PREMIUM_ECONOMY : CABIN_ECONOMY);
      return next;
    });
  }

  const totalPax = adult + child + infant;
  const totalPaxLabel = useMemo(() => {
    const parts: string[] = [];
    if (adult) parts.push(`${adult} adult${adult > 1 ? "s" : ""}`);
    if (child) parts.push(`${child} child${child > 1 ? "ren" : ""}`);
    if (infant) parts.push(`${infant} infant${infant > 1 ? "s" : ""}`);
    return parts.join(" · ") || "Add travellers";
  }, [adult, child, infant]);

  const canSubmit =
    !!from.code && !!to.code &&
    from.code !== to.code &&
    totalPax >= 1 && !!outboundDate &&
    (trip !== "round_trip" || (!!inboundDate && inboundDate >= outboundDate));

  const search = useMutation({
    mutationFn: async () => {
      const payload = {
        direct_flight: direct,
        adult_count: adult,
        child_count: child,
        infant_count: infant,
        flight_cabin_class: cabin,
        journey_type: trip === "one_way" ? 1 : 2,
        preferred_departure_time: `${outboundDate}T00:00:00`,
        ...(trip === "round_trip"
          ? { preferred_return_departure_time: `${inboundDate}T00:00:00` }
          : {}),
        origin: from.code,
        destination: to.code,
        currency: partnerCurrency,
        source,
      };
      const resp = await api.post<SearchResponse>("/v1/flights/search/", payload);

      // "No flights on this route" comes back as a 200 with empty lists — a
      // valid answer, not an error. Turn it into one here so the empty state
      // below renders instead of navigating to a Results page with nothing on
      // it. Nuitee hits this far more often than TravClan, since an offerless
      // route is an ordinary outcome rather than an upstream fault.
      const optionCount = (resp.outbound?.length ?? 0) + (resp.inbound?.length ?? 0);
      if (optionCount === 0) {
        throw new ApiError(200, resp, resp.detail || "No flights found for this search.");
      }

      saveSearch({
        form: {
          journey_type: trip,
          origin: from.code,
          destination: to.code,
          outbound_date: outboundDate,
          inbound_date: trip === "round_trip" ? inboundDate : undefined,
          adult, child, infant,
          cabin_class: cabin,
          currency: partnerCurrency,
          source,
        },
        response: resp,
        fetched_at: Date.now(),
      });
      return resp;
    },
    onSuccess: () => nav("/results"),
  });

  const errMsg = search.error instanceof ApiError ? search.error.message : null;
  const dep = fmtPretty(outboundDate);
  const ret = fmtPretty(inboundDate);

  function swap() {
    setFrom(to);
    setTo(from);
  }

  return (
    <>
      {search.isPending && (
        <SearchingLoader
          origin={from.code}
          destination={to.code}
          isRoundTrip={trip === "round_trip"}
          depart={`${fmtPretty(outboundDate).sub.split(" · ")[0]}, ${fmtPretty(outboundDate).value}`}
          return_={trip === "round_trip" ? `${fmtPretty(inboundDate).sub.split(" · ")[0]}, ${fmtPretty(inboundDate).value}` : undefined}
          paxLabel={totalPaxLabel}
          cabinLabel={CABIN_OPTIONS.find((c) => c.value === cabin)?.label || "Economy"}
        />
      )}
      <section className="flights-hero">
        <div className="container">
          <div className="page-head">
            <div className="eyebrow">
              <Link to="/">Home</Link>
              <span className="crumb-sep">›</span>
              <b>Flights</b>
            </div>
            <h1>Where are we <span className="serif">going</span>?</h1>
            <p className="lede">
              One form covers domestic and international, one-way and round-trip. Pick your
              shape, fill the boxes, and Kaira finds the rest.
            </p>
          </div>

          <div className="search-card" ref={cardRef}>
            <div className="search-head">
              <div className="search-tabs">
                {([
                  ["one_way", "One-way"],
                  ["round_trip", "Round-trip"],
                ] as Array<[Trip, string]>).map(([t, label]) => (
                  <button
                    key={t}
                    type="button"
                    className={`search-tab${trip === t ? " active" : ""}`}
                    onClick={() => setTrip(t)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <SourcePicker
                value={source}
                onChange={setSource}
                isActive={active === "source"}
                onActivate={() => setActive("source")}
                onClose={() => setActive(null)}
              />
            </div>

            <div className="search-fields-row">
              <AirportPicker
                label="From"
                value={from}
                onChange={setFrom}
                isActive={active === "from"}
                onActivate={() => setActive("from")}
                onClose={() => setActive(null)}
              />

              <button type="button" className="swap-inline" aria-label="Swap origin and destination" onClick={swap}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 16V4M7 4L3 8M7 4l4 4M17 8v12M17 20l-4-4M17 20l4-4" />
                </svg>
              </button>

              <AirportPicker
                label="To"
                value={to}
                onChange={setTo}
                isActive={active === "to"}
                onActivate={() => setActive("to")}
                onClose={() => setActive(null)}
              />

              <FieldBox
                isActive={active === "depart"}
                onClick={() => setActive("depart")}
                label="Depart"
                value={dep.value}
                sub={dep.sub}
                popover={active === "depart" ? (
                  <CalendarPicker
                    selected={ymdToDate(outboundDate)}
                    minDate={new Date()}
                    onSelect={(d) => { setOutboundDate(dateToYmd(d)); setActive(null); }}
                  />
                ) : null}
              />

              {trip === "round_trip" && (
                <FieldBox
                  isActive={active === "return"}
                  onClick={() => setActive("return")}
                  label="Return"
                  value={ret.value}
                  sub={ret.sub}
                  popover={active === "return" ? (
                    <CalendarPicker
                      selected={ymdToDate(inboundDate)}
                      minDate={ymdToDate(outboundDate)}
                      onSelect={(d) => { setInboundDate(dateToYmd(d)); setActive(null); }}
                    />
                  ) : null}
                />
              )}

              <FieldBox
                isActive={active === "travellers"}
                onClick={() => setActive("travellers")}
                label="Travellers · cabin"
                value={totalPaxLabel}
                sub={CABIN_OPTIONS.find((c) => c.value === cabin)?.label || "Economy"}
                popover={active === "travellers" ? (
                  <div className="field-popover" style={{ minWidth: 320 }}>
                    <Stepper label="Adults" min={1} value={adult} onChange={setAdult} />
                    <Stepper label="Children" min={0} value={child} onChange={setChild} />
                    <Stepper label="Infants" min={0} max={adult} value={infant} onChange={setInfant} />
                    <div className="field-popover-row">
                      <span className="label">Cabin</span>
                      <select value={cabin} onChange={(e) => setCabin(parseInt(e.target.value, 10))}>
                        {CABIN_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                  </div>
                ) : null}
              />
            </div>

            {errMsg && (
              <div className="empty-state" style={{ margin: "18px 0 0", padding: "20px 22px" }}>
                <span className="emoji">✈️</span>
                <h3>No flights for this <span className="serif">search</span></h3>
                <p>The provider couldn't return results for these dates. The exact message:</p>
                <div className="api-msg">{errMsg}</div>
                <p style={{ fontSize: 12, marginTop: 0 }}>Try a different date, route, or fewer passengers.</p>
              </div>
            )}

            <div className="search-foot">
              <div className="search-quickrow">
                <button type="button" className={`qpill${direct ? " on" : ""}`} onClick={() => setDirect((v) => !v)}>
                  Non-stop only
                  {direct && <span className="qpill-state">on</span>}
                </button>
                <button type="button" className={`qpill${refundable ? " on" : ""}`} onClick={() => setRefundable((v) => !v)}>
                  Refundable only
                  {refundable && <span className="qpill-state">on</span>}
                </button>
                <button
                  type="button"
                  className={`qpill${trip === "round_trip" ? " on" : ""}`}
                  onClick={() => setTrip((t) => (t === "round_trip" ? "one_way" : "round_trip"))}
                >
                  Add return
                </button>
                <button
                  type="button"
                  className={`qpill${infant > 0 ? " on" : ""}`}
                  onClick={() => setInfant((n) => (n > 0 ? 0 : 1))}
                >
                  Add infant
                </button>
                <button type="button" className={`qpill${premium ? " on" : ""}`} onClick={onTogglePremium}>
                  Premium economy
                </button>
              </div>
              <button
                type="button"
                className={`search-cta${search.isPending ? " loading" : ""}`}
                disabled={!canSubmit || search.isPending}
                onClick={() => canSubmit && search.mutate()}
                style={{ opacity: canSubmit || search.isPending ? 1 : 0.55 }}
              >
                Search flights
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          <div className="nudges">
            <Nudge onClick={() => applyNudge("goa-weekend")}>weekend in Goa for 2 — 16th to 19th?</Nudge>
            <Nudge onClick={() => applyNudge("dubai-tuesday")}>cheapest Tuesday to Dubai this month</Nudge>
            <Nudge onClick={() => applyNudge("london-business")}>Delhi to London in business class</Nudge>
            <Nudge onClick={() => applyNudge("bangalore-redeye")}>red-eye to Bangalore tonight</Nudge>
          </div>
        </div>
      </section>

      <section className="inspire-section">
        <div className="container">
          <div className="inspire-head">
            <div className="k-avatar" />
            <h2>Where Kaira's <span className="serif">sending</span> people this week.</h2>
          </div>
          <div className="inspire-grid">
            <InspireCard variant="bali"   emoji="🌴" route="Delhi → Denpasar"     name="Bali · monsoon edits"     meta="4 dates · direct"  amount="₹ 28,400" />
            <InspireCard variant="tokyo"  emoji="⛩️" route="Mumbai → Tokyo"        name="Tokyo · sakura tail"      meta="2 dates · 1 stop"  amount="₹ 52,300" />
            <InspireCard variant="dubai"  emoji="🏜️" route="Bangalore → Dubai"     name="Dubai · long weekend"     meta="8 dates · direct"  amount="₹ 18,400" />
            <InspireCard variant="london" emoji="🌧️" route="Delhi → London"        name="London · slow summer"     meta="5 dates · direct"  amount="₹ 68,200" />
          </div>
        </div>
      </section>
    </>
  );

  function applyNudge(id: "goa-weekend" | "dubai-tuesday" | "london-business" | "bangalore-redeye") {
    if (id === "goa-weekend") {
      setTrip("round_trip");
      setFrom(lookupAirport("DEL")); setTo({ code: "GOI", city: "Goa", name: "Goa Intl" });
      setOutboundDate(nextWeekday(5)); setInboundDate(nextWeekday(1));
      setAdult(2); setChild(0); setInfant(0); setCabin(CABIN_ECONOMY);
    } else if (id === "dubai-tuesday") {
      setTrip("one_way");
      setFrom(lookupAirport("DEL")); setTo({ code: "DXB", city: "Dubai", name: "Dubai Intl" });
      setOutboundDate(nextWeekday(2)); setAdult(1); setChild(0); setInfant(0); setCabin(CABIN_ECONOMY);
    } else if (id === "london-business") {
      setTrip("one_way");
      setFrom(lookupAirport("DEL")); setTo({ code: "LHR", city: "London", name: "Heathrow" });
      setOutboundDate(defaultDate(14)); setCabin(4);
    } else if (id === "bangalore-redeye") {
      setTrip("one_way");
      setFrom(lookupAirport("DEL")); setTo({ code: "BLR", city: "Bangalore", name: "Kempegowda Intl" });
      setOutboundDate(defaultDate(0));
    }
  }
}

// ─── helpers ────────────────────────────────────────────────────────────────

function nextWeekday(targetWeekday: number): string {
  const d = new Date();
  const diff = (targetWeekday - d.getDay() + 7) % 7 || 7;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function FieldBox(props: {
  isActive: boolean;
  onClick: () => void;
  label: string;
  value: React.ReactNode;
  sub: React.ReactNode;
  popover?: React.ReactNode;
}) {
  return (
    <div
      className={`field-box${props.isActive ? " active" : ""}`}
      onClick={(e) => { e.stopPropagation(); props.onClick(); }}
    >
      <div className="label">{props.label}</div>
      <div className="value">{props.value}</div>
      <div className="sub">{props.sub}</div>
      {props.popover}
    </div>
  );
}

function Stepper(props: { label: string; value: number; onChange: (v: number) => void; min?: number; max?: number }) {
  const min = props.min ?? 0;
  const max = props.max ?? 9;
  return (
    <div className="field-popover-row">
      <span className="label">{props.label}</span>
      <span className="stepper">
        <button type="button" disabled={props.value <= min} onClick={() => props.onChange(Math.max(min, props.value - 1))}>−</button>
        <span className="n">{props.value}</span>
        <button type="button" disabled={props.value >= max} onClick={() => props.onChange(Math.min(max, props.value + 1))}>+</button>
      </span>
    </div>
  );
}

function Nudge(props: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button type="button" className="nudge" onClick={props.onClick}>
      <span className="k-mini">K</span>
      {props.children}
    </button>
  );
}

function InspireCard(props: { variant: "bali" | "tokyo" | "dubai" | "london"; emoji: string; route: string; name: string; meta: string; amount: string }) {
  return (
    <article className="inspire-card">
      <div className={`inspire-img ${props.variant}`}>
        <span className="inspire-emoji">{props.emoji}</span>
      </div>
      <div className="inspire-body">
        <div className="route">{props.route}</div>
        <h4>{props.name}</h4>
        <div className="meta">
          <span>{props.meta}</span>
          <b>{props.amount}</b>
        </div>
      </div>
    </article>
  );
}
